import React, { useState, useEffect } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Unstable_Grid2';
import { Typography, Box, Paper, Chip, Divider, Alert } from '@mui/material';
import { useTheme } from '@emotion/react';
import EditIcon from '@mui/icons-material/Edit';
import HomeIcon from '@mui/icons-material/Home';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import BalconyIcon from '@mui/icons-material/Balcony';
import YardIcon from '@mui/icons-material/Yard';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import SelectSky from '../newEDL/components/form/atom/SelectSky';
import { useFormik } from 'formik';
import { cloneDeep } from 'lodash';
import dayjs from 'dayjs';

export default function DialogModifyAnnexesAdmin({ handleDialog, datasEdl }) {
  const theme = useTheme();
  const [open, setOpen] = useState(true);
  const [newPossession, setNewPossession] = useState(null);

  const handleClose = (btn) => {
    if (typeof btn === 'string') {
      setOpen(false);
      handleDialog({ btn, newPossession });
    }
  };

  const internFormik = useFormik({
    initialValues: datasEdl,
  });

  useEffect(() => {
    if (internFormik?.values?.possession) {
      let possession = cloneDeep(internFormik.values.possession);
      const annexes = possession.annexes;

      // Reconstruire les tableaux caves et parkings en fonction des valeurs nbCaves et nbParkings
      const nbCaves = annexes.nbCaves ?? 0;
      const nbParkings = annexes.nbParkings ?? 0;

      // Créer les tableaux avec les bons numéros (préserver les existants si possible)
      const caves = [];
      for (let i = 0; i < nbCaves; i++) {
        caves.push(annexes.caves?.[i] ?? `Cave ${i + 1}`);
      }

      const parkings = [];
      for (let i = 0; i < nbParkings; i++) {
        parkings.push(annexes.parkings?.[i] ?? `Parking ${i + 1}`);
      }

      const newPoss = {
        annexes: {
          ...annexes,
          caves,
          parkings,
        },
        area: possession.area,
        typeHouse: possession.typeHouse,
        nbRoom: possession.nbRoom,
      };
      setNewPossession(newPoss);
    }
  }, [internFormik.values.possession]);

  const optionsNbAnnexes = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
  const optionsNbRoom = ['T1', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'T8'];

  // Calcul des différences
  const getDiff = (oldVal, newVal) => {
    const oldNum = Array.isArray(oldVal) ? oldVal.length : oldVal;
    const newNum = Array.isArray(newVal) ? newVal.length : newVal;
    return newNum - oldNum;
  };

  const currentAnnexes = datasEdl.possession.annexes;
  const newAnnexes = newPossession?.annexes;

  const annexesConfig = [
    { key: 'caves', label: 'Caves', icon: <WarehouseIcon />, isArray: true },
    { key: 'parkings', label: 'Parkings', icon: <DirectionsCarIcon />, isArray: true },
    { key: 'nbBalcony', label: 'Balcons', icon: <BalconyIcon />, isArray: false },
    { key: 'nbGarden', label: 'Jardins', icon: <YardIcon />, isArray: false },
    { key: 'nbOther', label: 'Autres', icon: <AddCircleIcon />, isArray: false },
  ];

  const hasChanges = newPossession && (
    newPossession.nbRoom !== datasEdl.possession.nbRoom ||
    annexesConfig.some(({ key, isArray }) => {
      const oldVal = isArray ? currentAnnexes[key].length : currentAnnexes[key];
      const newVal = isArray ? newAnnexes?.[key]?.length : newAnnexes?.[key];
      return oldVal !== newVal;
    })
  );

  const DiffChip = ({ diff }) => {
    if (diff === 0) return null;
    return (
      <Chip
        size="small"
        label={diff > 0 ? `+${diff}` : diff}
        color={diff > 0 ? 'success' : 'error'}
        sx={{ ml: 1, fontWeight: 'bold' }}
      />
    );
  };

  const AnnexeRow = ({ config, formik }) => {
    const { key, label, icon, isArray } = config;
    const currentVal = isArray ? currentAnnexes[key].length : currentAnnexes[key];
    const newVal = isArray ? newAnnexes?.[key]?.length ?? currentVal : newAnnexes?.[key] ?? currentVal;
    const diff = newVal - currentVal;

    return (
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 1,
          backgroundColor: diff !== 0 ? (diff > 0 ? 'success.light' : 'error.light') : 'grey.100',
          border: diff !== 0 ? `2px solid ${diff > 0 ? theme.palette.success.main : theme.palette.error.main}` : '1px solid #e0e0e0',
          transition: 'all 0.3s ease',
        }}
      >
        <Grid container alignItems="center" spacing={2}>
          <Grid xs={1}>
            <Box sx={{ color: diff !== 0 ? (diff > 0 ? 'success.dark' : 'error.dark') : 'grey.600' }}>
              {icon}
            </Box>
          </Grid>
          <Grid xs={3}>
            <Typography variant="subtitle1" fontWeight="medium">
              {label}
            </Typography>
          </Grid>
          <Grid xs={2}>
            <Typography variant="body2" color="text.secondary">
              Actuel: <strong>{currentVal}</strong>
            </Typography>
          </Grid>
          <Grid xs={4}>
            {isArray ? (
              <SelectSky
                name={`possession.annexes.nb${key.charAt(0).toUpperCase() + key.slice(1)}`}
                label=""
                options={optionsNbAnnexes}
                value={formik.values.possession.annexes[`nb${key.charAt(0).toUpperCase() + key.slice(1)}`]}
                formik={formik}
                size="small"
              />
            ) : (
              <SelectSky
                name={`possession.annexes.${key}`}
                label=""
                options={optionsNbAnnexes}
                value={formik.values.possession.annexes[key]}
                formik={formik}
                size="small"
              />
            )}
          </Grid>
          <Grid xs={2} textAlign="right">
            <DiffChip diff={diff} />
          </Grid>
        </Grid>
      </Paper>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={() => handleClose('cancelFinishEdl')}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: '60vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: theme.palette.primary.main,
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <EditIcon />
        Modification des annexes (Admin)
      </DialogTitle>

      <DialogContent sx={{ mt: 2 }}>
        {/* Résumé de l'EDL */}
        <Paper elevation={2} sx={{ p: 2, mb: 3, backgroundColor: 'grey.50' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid xs={12} sm={6}>
              <Box display="flex" alignItems="center" gap={1}>
                <HomeIcon color="primary" />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Adresse
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {datasEdl.possession.address.street}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {datasEdl.possession.address.zipCode} {datasEdl.possession.address.locality}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary">
                Type EDL
              </Typography>
              <Chip
                size="small"
                label={datasEdl.manage.typeEDL === 'entrant' ? 'Entrant' : 'Sortant'}
                color={datasEdl.manage.typeEDL === 'entrant' ? 'success' : 'warning'}
                sx={{ display: 'block', mt: 0.5 }}
              />
            </Grid>
            <Grid xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary">
                Date EDL
              </Typography>
              <Typography variant="body2" fontWeight="medium">
                {dayjs(datasEdl.manage.dateEDL).format('DD/MM/YYYY')}
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        {/* Nombre de pièces */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 2,
            backgroundColor: newPossession?.nbRoom !== datasEdl.possession.nbRoom ? 'info.light' : 'grey.100',
            border: newPossession?.nbRoom !== datasEdl.possession.nbRoom ? `2px solid ${theme.palette.info.main}` : '1px solid #e0e0e0',
          }}
        >
          <Grid container alignItems="center" spacing={2}>
            <Grid xs={1}>
              <MeetingRoomIcon color={newPossession?.nbRoom !== datasEdl.possession.nbRoom ? 'info' : 'action'} />
            </Grid>
            <Grid xs={3}>
              <Typography variant="subtitle1" fontWeight="medium">
                Nombre de pièces
              </Typography>
            </Grid>
            <Grid xs={2}>
              <Typography variant="body2" color="text.secondary">
                Actuel: <strong>{datasEdl.possession.nbRoom}</strong>
              </Typography>
            </Grid>
            <Grid xs={4}>
              <SelectSky
                name="possession.nbRoom"
                label=""
                options={optionsNbRoom}
                value={internFormik.values.possession.nbRoom}
                formik={internFormik}
                size="small"
              />
            </Grid>
            <Grid xs={2} textAlign="right">
              {newPossession?.nbRoom !== datasEdl.possession.nbRoom && (
                <Chip size="small" label="Modifié" color="info" />
              )}
            </Grid>
          </Grid>
        </Paper>

        <Divider sx={{ my: 2 }}>
          <Typography variant="overline" color="text.secondary">
            Annexes
          </Typography>
        </Divider>

        {/* Liste des annexes */}
        {annexesConfig.map((config) => (
          <AnnexeRow key={config.key} config={config} formik={internFormik} />
        ))}

        {/* Message de confirmation */}
        {hasChanges && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Les modifications seront appliquées immédiatement et la tarification sera recalculée automatiquement.
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button
          variant="outlined"
          onClick={() => handleClose('cancelFinishEdl')}
        >
          Annuler
        </Button>
        <Button
          variant="contained"
          onClick={() => handleClose('confirmModifyAnnexes')}
          disabled={!hasChanges}
          startIcon={<EditIcon />}
        >
          Confirmer les modifications
        </Button>
      </DialogActions>
    </Dialog>
  );
}
